import React, { useEffect, useMemo } from 'react';
import { usePortalProps } from '../ShadowPortalContext';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Typography,
  Alert,
  lightTheme,
  Button,
  FormLabel,
} from '@platform-ui/design-system';
import { ThemeProvider } from '@platform-ui/zds-next';
import { useTranslation } from 'react-i18next';
import { useMultiOrgCookies } from '../hooks/useMultiOrgCookies';
import { arrayToTree, TreeItem } from 'performant-array-to-tree';
import { decorateOrgsForTree, OrgTreeItem } from './utils';

type OrgItem = OrgTreeItem & {
  level: number;
};

function flattenTree(tree: TreeItem, level: number): OrgItem[] {
  return [
    { ...tree.data, level },
    ...tree.children.flatMap((child) => flattenTree(child, level + 1)),
  ];
}

export const WorkingOrgModal = ({
  open,
  onClose,
  organizations,
  userOrgIds,
  reloadPage,
}) => {
  const { t } = useTranslation();
  const buildPortalProps = usePortalProps();
  const { workingOrg, setWorkingOrg, selectedOrgIds, setSelectedOrgIds } =
    useMultiOrgCookies();
  const [selectedOrg, setSelectedOrg] = React.useState<OrgItem | null>(null);
  const flattenedTree = useMemo(() => {
    const orgs = decorateOrgsForTree(organizations, userOrgIds, '', []);
    const tree = arrayToTree(orgs, {
      id: 'organizationId',
      parentId: 'parentId',
    });

    return tree.flatMap((t) => flattenTree(t, 0));
  }, [organizations, userOrgIds]);

  useEffect(() => {
    const org = flattenedTree.find((org) => org.organizationId === workingOrg);
    if (org) {
      setSelectedOrg(org);
    }
  }, [workingOrg]);

  const saveWorkingOrg = () => {
    if (selectedOrg) {
      setWorkingOrg(selectedOrg?.organizationId);
      if (!selectedOrgIds?.includes(selectedOrg?.organizationId)) {
        setSelectedOrgIds([...selectedOrgIds, selectedOrg?.organizationId]);
        reloadPage();
      }
    }
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <Dialog {...buildPortalProps()} fullWidth open={open} onClose={onClose}>
        <DialogTitle>{t('MultiOrg.OrgSettingsModal.Title')}</DialogTitle>
        <DialogContent>
          <Alert open severity="info">
            <Typography variant="body2">
              {t('MultiOrg.OrgSettingsModal.AlertBody')}
            </Typography>
          </Alert>
          <FormLabel>{t('MultiOrg.OrgSettingsModal.SelectOrg')}</FormLabel>
          <Select
            MenuProps={buildPortalProps()}
            sx={{ mt: 1 }}
            fullWidth
            value={selectedOrg}
            onChange={(e) => {
              setSelectedOrg(e.target.value as OrgItem);
            }}
            renderValue={(value) => (
              <Typography variant="body2">{value?.organizationName}</Typography>
            )}
          >
            {flattenedTree.map((org) => (
              <MenuItem
                sx={{ paddingLeft: `${16 + org.level * 16}px` }}
                key={org.organizationId}
                value={org as any}
                disabled={org.disabled}
              >
                <Typography variant="body2">{org.organizationName}</Typography>
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button variant="tertiary" dsOnClick={onClose}>
            {t('MultiOrg.OrgSettingsModal.Cancel')}
          </Button>
          <Button
            dsOnClick={() => {
              saveWorkingOrg();
              onClose();
            }}
          >
            {t('MultiOrg.OrgSettingsModal.Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};
